import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Popover, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Replay10OutlinedIcon from "@mui/icons-material/Replay10Outlined";
import Forward10OutlinedIcon from "@mui/icons-material/Forward10Outlined";
import Tooltip from "@mui/material/Tooltip";
import {
  formatLocalDate,
  formatLocalTime,
  formatLocalDateTime,
} from "../../utils/playbackHelper";

const CenterControls = ({
  playbackTime,
  eventDates,
  onApplyTime,
  onCalendarMonthChange,
  onShiftPlayback,
}) => {
  const theme = useTheme();
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [calendarViewDate, setCalendarViewDate] = useState(() => new Date(playbackTime));
  const [calendarTempTime, setCalendarTempTime] = useState(playbackTime);
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  const handleCalendarOpen = (event) => {
    setCalendarTempTime(playbackTime);
    setCalendarViewDate(new Date(playbackTime));
    onCalendarMonthChange(playbackTime);
    setTimePickerOpen(false);
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setTimePickerOpen(false);
    setCalendarAnchorEl(null);
  };

  const handleDateSelect = (dateString) => {
    const prev = new Date(calendarTempTime);
    const [year, month, day] = dateString.split("-").map(Number);
    const sameDay = dateString === formatLocalDate(prev);
    // Switching date resets Specific Time to 00:00:00; same day keeps adjusted time.
    const updated = new Date(
      year,
      month - 1,
      day,
      sameDay ? prev.getHours() : 0,
      sameDay ? prev.getMinutes() : 0,
      sameDay ? prev.getSeconds() : 0,
      sameDay ? prev.getMilliseconds() : 0
    );
    setCalendarTempTime(updated.getTime());
  };

  const updateSpecificTimeField = (field, value) => {
    const prev = new Date(calendarTempTime);
    const num = Math.max(0, parseInt(value, 10) || 0);
    const updated = new Date(prev);
    if (field === "hour") updated.setHours(Math.min(23, num));
    if (field === "minute") updated.setMinutes(Math.min(59, num));
    if (field === "second") updated.setSeconds(Math.min(59, num));
    setCalendarTempTime(updated.getTime());
  };

  const changeCalendarMonth = (offset) => {
    const next = new Date(calendarViewDate);
    next.setMonth(next.getMonth() + offset);
    setCalendarViewDate(next);
    onCalendarMonthChange(next.getTime());
  };

  const handleApply = () => {
    onApplyTime(calendarTempTime);
    handleCalendarClose();
  };

  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        zIndex: 3,
        pointerEvents: "auto",
      }}
    >
      <Tooltip title="Rewind 10 seconds">
        <IconButton
          size="small"
          onClick={() => onShiftPlayback(-10)}
          sx={{
            color: "#24336f",
            p: 0.35,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Replay10OutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>
      <Box
        onClick={handleCalendarOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") handleCalendarOpen(event);
        }}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          px: 1.2,
          py: 0.4,
          border: "1px solid #c4c8d0",
          borderRadius: "6px",
          color: "#303030",
          cursor: "pointer",
          backgroundColor: "#fff",
          userSelect: "none",
          transition: "all 0.15s ease",
          "&:hover": {
            borderColor: "#24336f",
            boxShadow: "0 1px 4px rgba(36, 51, 111, 0.1)",
          },
        }}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: "#555" }} />
        <Typography sx={{ fontSize: 11, fontWeight: 500, whiteSpace: "nowrap", lineHeight: 1.2 }}>
          {formatLocalDateTime(new Date(playbackTime), true)}
        </Typography>
      </Box>
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={handleCalendarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              mb: 1,
              p: 0,
              borderRadius: "8px",
              boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
              border: "1px solid #d5d9e0",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              overflow: "visible",
            },
          },
        }}
      >
        {/* Left Column: Calendar Panel */}
        <Box sx={{ width: 280, p: 2, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton size="small" onClick={() => changeCalendarMonth(-12)} sx={{ p: 0.25, borderRadius: "6px" }}>
                <FirstPageIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={() => changeCalendarMonth(-1)} sx={{ p: 0.25, borderRadius: "6px" }}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
              {calendarViewDate.toLocaleDateString([], {
                year: "numeric",
                month: "short",
              })}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton size="small" onClick={() => changeCalendarMonth(1)} sx={{ p: 0.25, borderRadius: "6px" }}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={() => changeCalendarMonth(12)} sx={{ p: 0.25, borderRadius: "6px" }}>
                <LastPageIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              rowGap: 0.75,
              columnGap: 0.25,
              textAlign: "center",
              mb: 1,
            }}
          >
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <Typography
                key={`${day}-${index}`}
                sx={{
                  fontSize: 11,
                  color: "#7b8190",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                }}
              >
                {day}
              </Typography>
            ))}
            {Array.from({
              length: new Date(
                calendarViewDate.getFullYear(),
                calendarViewDate.getMonth(),
                1
              ).getDay(),
            }).map((_, index) => (
              <Box key={`blank-${index}`} sx={{ height: 28 }} />
            ))}
            {Array.from(
              {
                length: new Date(
                  calendarViewDate.getFullYear(),
                  calendarViewDate.getMonth() + 1,
                  0
                ).getDate(),
              },
              (_, index) => {
                const day = index + 1;
                const currentVal = `${calendarViewDate.getFullYear()}-${String(
                  calendarViewDate.getMonth() + 1
                ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const isCurrentSelected =
                  currentVal === formatLocalDate(new Date(calendarTempTime));
                const hasEvent = eventDates.includes(currentVal);
                return (
                  <Box
                    key={currentVal}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      size="small"
                      disableRipple
                      onClick={() => handleDateSelect(currentVal)}
                      sx={{
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        maxWidth: 28,
                        minHeight: 28,
                        maxHeight: 28,
                        p: 0,
                        borderRadius: "50%",
                        fontSize: 11,
                        fontWeight: isCurrentSelected ? 700 : 500,
                        color: isCurrentSelected ? "#fff" : "#303846",
                        backgroundColor: isCurrentSelected ? theme.palette.primary.main : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        lineHeight: 1,
                        "&:hover": {
                          backgroundColor: isCurrentSelected
                            ? theme.palette.primary.main
                            : "#eef1f6",
                        },
                      }}
                    >
                      {day}
                      {hasEvent && (
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: 2,
                            width: 3,
                            height: 3,
                            borderRadius: "50%",
                            backgroundColor: isCurrentSelected ? "#fff" : "#555",
                            transform: "translateX(-50%)",
                          }}
                        />
                      )}
                    </Button>
                  </Box>
                );
              }
            )}
          </Box>

          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid #e2e2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, color: "#666", whiteSpace: "nowrap" }}>
              Specific Time
            </Typography>
            <Box
              onClick={() => setTimePickerOpen((open) => !open)}
              role="button"
              tabIndex={0}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.8,
                py: 0.35,
                border: `1px solid ${timePickerOpen ? "#24336f" : "#d8d8d8"}`,
                borderRadius: "6px",
                backgroundColor: timePickerOpen ? "#f5f7fc" : "#fafafa",
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "#24336f",
                  backgroundColor: "#f5f7fc",
                },
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 14, color: "#666" }} />
              <Typography sx={{ fontSize: 11, color: "#333", fontWeight: 600 }}>
                {formatLocalTime(new Date(calendarTempTime), true)}
              </Typography>
            </Box>
          </Box>

          {/* Dual Action Buttons: Cancel & Apply */}
          <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
            <Button
              fullWidth
              size="small"
              onClick={handleCalendarClose}
              sx={{
                py: 0.5,
                border: "1px solid #c4c8d0",
                backgroundColor: "#fff",
                color: "#555",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#999",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              size="small"
              onClick={handleApply}
              sx={{
                py: 0.5,
                backgroundColor: "#333",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#111" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        {/* Right Column: Time Picker Panel (Side-by-Side, Top-aligned) */}
        {timePickerOpen && (
          <Box
            sx={{
              width: 215,
              p: 2,
              borderLeft: "1px solid #e5e8ed",
              backgroundColor: "#fafbfd",
              borderRadius: "0 8px 8px 0",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#24336f" }}>
                Select Time
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  const reset = new Date(calendarTempTime);
                  reset.setHours(0, 0, 0, 0);
                  setCalendarTempTime(reset.getTime());
                }}
                sx={{
                  fontSize: 10,
                  py: 0.15,
                  px: 0.75,
                  minWidth: 0,
                  textTransform: "none",
                  color: "#555",
                  backgroundColor: "#eaedf2",
                  borderRadius: "4px",
                  "&:hover": { backgroundColor: "#dde1e8" },
                }}
              >
                Reset 00:00:00
              </Button>
            </Box>

            {/* Hour & Minute Scroll Lists */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              {/* Hour column */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography sx={{ fontSize: 10, color: "#777", fontWeight: 700, mb: 0.5 }}>
                  Hour
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: 130,
                    overflowY: "auto",
                    border: "1px solid #e0e3e8",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    p: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const val = String(i).padStart(2, "0");
                    const isSelected = new Date(calendarTempTime).getHours() === i;
                    return (
                      <Box
                        key={val}
                        onClick={() => updateSpecificTimeField("hour", i)}
                        sx={{
                          py: 0.35,
                          fontSize: 11,
                          fontWeight: isSelected ? 700 : 500,
                          textAlign: "center",
                          borderRadius: "4px",
                          cursor: "pointer",
                          color: isSelected ? "#fff" : "#333",
                          backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected ? theme.palette.primary.main : "#eaedf3",
                          },
                        }}
                      >
                        {val}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Minute column */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography sx={{ fontSize: 10, color: "#777", fontWeight: 700, mb: 0.5 }}>
                  Min
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: 130,
                    overflowY: "auto",
                    border: "1px solid #e0e3e8",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    p: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  {Array.from({ length: 60 }, (_, i) => {
                    const val = String(i).padStart(2, "0");
                    const isSelected = new Date(calendarTempTime).getMinutes() === i;
                    return (
                      <Box
                        key={val}
                        onClick={() => updateSpecificTimeField("minute", i)}
                        sx={{
                          py: 0.35,
                          fontSize: 11,
                          fontWeight: isSelected ? 700 : 500,
                          textAlign: "center",
                          borderRadius: "4px",
                          cursor: "pointer",
                          color: isSelected ? "#fff" : "#333",
                          backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected ? theme.palette.primary.main : "#eaedf3",
                          },
                        }}
                      >
                        {val}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Seconds Input Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pt: 1,
                pb: 0.5,
                borderTop: "1px solid #e8ebf0",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#666", fontWeight: 600 }}>
                Second (00-59)
              </Typography>
              <Box
                component="input"
                type="number"
                min="0"
                max="59"
                value={String(new Date(calendarTempTime).getSeconds()).padStart(2, "0")}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0));
                  updateSpecificTimeField("second", val);
                }}
                sx={{
                  width: 58,
                  height: 28,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                  py: 0.25,
                  px: 0.5,
                  border: "1px solid #c4c8d0",
                  borderRadius: "4px",
                  outline: "none",
                  backgroundColor: "#fff",
                  "&:focus": { borderColor: "#24336f" },
                }}
              />
            </Box>

            <Button
              fullWidth
              size="small"
              onClick={() => setTimePickerOpen(false)}
              sx={{
                mt: "auto",
                py: 0.5,
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#172664" },
              }}
            >
              Confirm
            </Button>
          </Box>
        )}
      </Popover>
      <Tooltip title="Forward 10 seconds">
        <IconButton
          size="small"
          onClick={() => onShiftPlayback(10)}
          sx={{ color: "#24336f", p: 0.35 }}
        >
          <Forward10OutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

CenterControls.propTypes = {
  playbackTime: PropTypes.number.isRequired,
  eventDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  onApplyTime: PropTypes.func.isRequired,
  onCalendarMonthChange: PropTypes.func.isRequired,
  onShiftPlayback: PropTypes.func.isRequired,
};

export default CenterControls;
